import { User } from "pwapi";

import { CaseDetailState } from "./caseDetailReducer";
import {
  CASES_SUCCESS,
  CASES_FAILURE,
  CASES_REQUEST,
  WIPE_CASES,
  LOGIN_SUCCESS,
  SET_LAST_CASE_VIEWED,
  NAVIGATE_CASE_LIST_SYNC,
  CASE_ID_LIST_SUCCESS,
  CASE_ID_LIST_FAILURE,
  CASE_ID_LIST_REQUEST,
} from "../actions/constants";

export enum QueueEnum {
  All = "all",
  Active = "active",
  Working = "working",
  Flagged = "flagged",
  Finalized = "finalized",
  Screen = "screen",
  Screened = "screened",
  Preliminary = "preliminary",
  Ordered = "ordered",
}

export const SubQueueFilters = {
  today: { filter: "today", value: true, label: "Today" },
  tomorrow: { filter: "tomorrow", value: true, label: "Tomorrow" },
};

export const availableSubQueues = {
  [QueueEnum.Active]: ["today", "tomorrow"],
};

export enum CaseListDirection {
  Asc = "asc",
  Desc = "desc",
}

export type InitialCaseListFormState = {
  queue: QueueEnum;
  page: number;
  paginationPerPage: number;
  sortDirection: CaseListDirection;
  sortColumn: string;
  activeAssignmentName?: string;
  orderingDoctorName?: string;
};

export interface CaseListPaginationParams {
  queue: QueueEnum;
  page: number;
  paginationPerPage: number;
  limit?: number;
  sortColumn?: string;
  sortDirection: CaseListDirection;

  lastName?: string;
  firstName?: string;
  addedDate?: string;
  accession?: string;
  clinic?: string;
  includeSignedOut?: boolean | null;

  fields?: string[];
  allSlidesSuccess?: boolean;
  allOrderedDocumentsUploaded?: boolean;
  hasIncompleteOrders?: boolean;
  hasUnviewedSlides?: boolean;
}

export function initialFormState(
  queue = QueueEnum.All,
  user?: User
): InitialCaseListFormState {
  const defaultPageSize = localStorage.getItem("caseListDefaultPageSize");
  return {
    queue,
    page: 1,
    paginationPerPage: defaultPageSize ? JSON.parse(defaultPageSize) : 10,
    sortDirection:
      queue === QueueEnum.Active || queue === QueueEnum.Working
        ? CaseListDirection.Asc
        : CaseListDirection.Desc,
    sortColumn: "priority",
    activeAssignmentName:
      user?.user_type === "dermpath"
        ? `${user?.first_name} ${user?.last_name}`
        : undefined,
    orderingDoctorName:
      user?.user_type === "physician"
        ? `${user?.first_name} ${user?.last_name}`
        : undefined,
  };
}

export function userDefaultQueue(user?: User): QueueEnum {
  return user?.user_type === "physician"
    ? QueueEnum.Finalized
    : user?.user_type === "dermpath" || user?.is_superuser
    ? QueueEnum.Active
    : QueueEnum.All;
}

export interface CasesState {
  count: number;
  count_specimen?: number;
  next?: string;
  previous?: string;
  results: CaseDetailState[]; // What appears in the rows of the DataTable
  caseIdList: number[]; // case IDs with current parameters, except page=1 and paginationPerPage=INITIAL_FETCH_LIMIIT
  caseIdListPending?: boolean;
  lastCaseViewed?: number;
  lastRequestTimestamp: number;
  loading: boolean;
  error?: Error;
  currentCaseSearch: CaseListPaginationParams;
}

export const initialState: CasesState = {
  results: [],
  caseIdList: [],
  caseIdListPending: false,
  count: 0,
  count_specimen: 0,
  lastCaseViewed: undefined,
  lastRequestTimestamp: 0,
  loading: true,
  error: undefined,
  currentCaseSearch: initialFormState(),
};

const reducer = (state: CasesState = initialState, action: any): CasesState => {
  switch (action.type) {
    case CASES_REQUEST:
      return {
        ...state,
        results: [],
        count: 0,
        loading: true,
      };
    case CASES_SUCCESS:
      if (state.lastRequestTimestamp > action.payload.lastRequestTimestamp) {
        return state;
      }
      return {
        ...state,
        ...action.payload,
        loading: false,
      };
    case CASES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case CASE_ID_LIST_REQUEST:
      return {
        ...state,
        caseIdList: [],
        caseIdListPending: true,
      };
    case CASE_ID_LIST_SUCCESS:
      return {
        ...state,
        caseIdList: action.payload,
        caseIdListPending: false,
      };
    case CASE_ID_LIST_FAILURE:
      return {
        ...state,
        error: action.payload,
        caseIdList: [],
        caseIdListPending: false,
      };
    case SET_LAST_CASE_VIEWED:
      return {
        ...state,
        lastCaseViewed: action.payload,
      };
    case NAVIGATE_CASE_LIST_SYNC:
      return {
        ...state,
        lastCaseViewed: action.payload,
      };
    case WIPE_CASES:
      return { ...initialState };
    case LOGIN_SUCCESS:
      return {
        ...state,
        loading: false,
        error: undefined,
      };
    default:
      return state;
  }
};

export default reducer;
